'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface FormErrors {
  email?: string;
  password?: string;
}

export default function LoginPage() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ email?: boolean; password?: boolean }>({});
  const [successMessage, setSuccessMessage] = useState('');

  const validateEmail = (value: string): string | undefined => {
    if (!value.trim()) return 'E-Mail-Adresse ist erforderlich.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Bitte gib eine gültige E-Mail-Adresse ein.';
    return undefined;
  };

  const validatePassword = (value: string): string | undefined => {
    if (!value) return 'Passwort ist erforderlich.';
    if (isRegister && value.length < 8) return 'Passwort muss mindestens 8 Zeichen lang sein.';
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      email: validateEmail(email),
      password: validatePassword(password),
    };
    setErrors(newErrors);
    setTouched({ email: true, password: true });
    return !newErrors.email && !newErrors.password;
  };

  const handleBlur = (field: 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === 'email') setErrors((prev) => ({ ...prev, email: validateEmail(email) }));
    if (field === 'password') setErrors((prev) => ({ ...prev, password: validatePassword(password) }));
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (touched.email) setErrors((prev) => ({ ...prev, email: validateEmail(value) }));
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (touched.password) setErrors((prev) => ({ ...prev, password: validatePassword(value) }));
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading('email');
    setSuccessMessage('');
    try {
      if (isRegister) {
        await signUp(email, password);
        const msg = 'Konto erstellt! Willkommen.';
        setSuccessMessage(msg);
        toast.success(msg);
        setTimeout(() => router.push('/content-dashboard'), 800);
      } else {
        await signIn(email, password);
        const msg = 'Erfolgreich angemeldet!';
        setSuccessMessage(msg);
        toast.success(msg);
        setTimeout(() => router.push('/content-dashboard'), 800);
      }
    } catch (err: any) {
      const msg = err?.message || 'Anmeldung fehlgeschlagen. Bitte versuche es erneut.';
      toast.error(msg);
      setErrors((prev) => ({ ...prev, email: ' ', password: msg }));
      setTouched({ email: true, password: true });
    } finally {
      setLoading(null);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading('google');
    toast.info('Google-Anmeldung wird gestartet…');
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(err?.message || 'Google-Anmeldung fehlgeschlagen.');
      setLoading(null);
    }
  };

  const handleAppleLogin = async () => {
    setLoading('apple');
    toast.info('Apple-Anmeldung wird gestartet…');
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(err?.message || 'Apple-Anmeldung fehlgeschlagen.');
      setLoading(null);
    }
  };

  const inputClass = (field: 'email' | 'password') => {
    if (touched[field] && errors[field]) return 'input-punk border-red-500 focus:border-red-400';
    if (touched[field] && !errors[field] && (field === 'email' ? email : password))
      return 'input-punk border-green-600 focus:border-green-500';
    return 'input-punk';
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <AppLogo size={36} />
          <div>
            <span className="font-display font-800 text-sm tracking-widest text-accent uppercase block leading-none">SEARCH4</span>
            <span className="font-display font-700 text-sm tracking-widest text-white uppercase block leading-none">DISTORTION</span>
          </div>
        </div>

        {/* Headline */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-800 text-white mb-2 uppercase tracking-wider">
            {isRegister ? 'Konto erstellen' : 'Willkommen zurück'}
          </h1>
          <p className="text-xs text-muted leading-relaxed max-w-xs mx-auto">
            Weil die Welt kein weißes Rauschen braucht. Für alle, die keine Lust mehr auf Standard bei der Content Creation haben.
          </p>
        </div>

        {/* Card */}
        <div className="punk-card p-6 space-y-4">
          {/* Success Banner */}
          {successMessage && (
            <div
              className="flex items-center gap-3 p-3 bg-green-950/30 border border-green-700/40 rounded"
              role="alert"
              aria-live="assertive"
            >
              <CheckCircle size={15} className="text-green-400 flex-shrink-0" aria-hidden="true" />
              <p className="text-xs font-600 text-green-400">{successMessage}</p>
            </div>
          )}

          {/* Social Login */}
          <div className="space-y-3">
            <button
              onClick={handleGoogleLogin}
              disabled={loading !== null}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-border bg-surface hover:border-accent/40 hover:bg-surface-2 transition-all rounded text-sm font-500 text-white focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label="Mit Google-Konto anmelden"
              aria-busy={loading === 'google'}
            >
              {loading === 'google' ? (
                <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              )}
              Mit Google anmelden
            </button>

            <button
              onClick={handleAppleLogin}
              disabled={loading !== null}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-border bg-surface hover:border-accent/40 hover:bg-surface-2 transition-all rounded text-sm font-500 text-white focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label="Mit Apple-Konto anmelden"
              aria-busy={loading === 'apple'}
            >
              {loading === 'apple' ? (
                <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              )}
              Mit Apple anmelden
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3" role="separator" aria-label="oder">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted mono" aria-hidden="true">oder</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Email Form */}
          <form
            onSubmit={handleEmailAuth}
            className="space-y-3"
            noValidate
            aria-label={isRegister ? 'Registrierungsformular' : 'Anmeldeformular'}
          >
            <div>
              <label className="label-punk" htmlFor="login-email">E-Mail-Adresse</label>
              <div className="relative">
                <input
                  id="login-email"
                  type="email"
                  className={inputClass('email')}
                  placeholder="deine@email.de"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => handleBlur('email')}
                  aria-required="true"
                  aria-invalid={touched.email && !!errors.email}
                  aria-describedby={touched.email && errors.email ? 'email-error' : undefined}
                  autoComplete="email"
                />
                {touched.email && !errors.email && email && (
                  <CheckCircle size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 pointer-events-none" aria-hidden="true" />
                )}
                {touched.email && errors.email && (
                  <AlertCircle size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" aria-hidden="true" />
                )}
              </div>
              {touched.email && errors.email && errors.email.trim() && (
                <p
                  id="email-error"
                  className="flex items-center gap-1 text-xs text-red-400 mt-1 mono"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle size={11} aria-hidden="true" />
                  {errors.email}
                </p>
              )}
            </div>
            <div>
              <label className="label-punk" htmlFor="login-password">Passwort</label>
              <div className="relative">
                <input
                  id="login-password"
                  type="password"
                  className={inputClass('password')}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  onBlur={() => handleBlur('password')}
                  aria-required="true"
                  aria-invalid={touched.password && !!errors.password}
                  aria-describedby={touched.password && errors.password ? 'password-error' : undefined}
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                />
                {touched.password && !errors.password && password && (
                  <CheckCircle size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 pointer-events-none" aria-hidden="true" />
                )}
                {touched.password && errors.password && (
                  <AlertCircle size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 pointer-events-none" aria-hidden="true" />
                )}
              </div>
              {touched.password && errors.password && (
                <p
                  id="password-error"
                  className="flex items-center gap-1 text-xs text-red-400 mt-1 mono"
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle size={11} aria-hidden="true" />
                  {errors.password}
                </p>
              )}
              {isRegister && touched.password && !errors.password && password && (
                <p className="flex items-center gap-1 text-xs text-green-500 mt-1 mono" aria-live="polite">
                  <CheckCircle size={11} aria-hidden="true" />
                  Passwort ist stark genug.
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading !== null}
              className="btn-primary w-full justify-center focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label={isRegister ? 'Neues Konto erstellen' : 'Mit E-Mail anmelden'}
              aria-busy={loading === 'email'}
            >
              {loading === 'email' ? (
                <>
                  <div className="w-4 h-4 border border-black border-t-transparent rounded-full animate-spin" aria-hidden="true" />
                  {isRegister ? 'Konto wird erstellt…' : 'Anmelden…'}
                </>
              ) : (
                isRegister ? 'Konto erstellen' : 'Anmelden'
              )}
            </button>
          </form>

          <p className="text-center text-xs text-muted">
            {isRegister ? 'Bereits ein Konto?' : 'Noch kein Konto?'}{' '}
            <button
              onClick={() => { setIsRegister(!isRegister); setErrors({}); setTouched({}); setSuccessMessage(''); }}
              className="text-accent hover:text-white transition-colors"
            >
              {isRegister ? 'Anmelden' : 'Registrieren'}
            </button>
          </p>
        </div>

        <p className="text-center text-xs text-muted-2 mt-6 leading-relaxed">
          Mit der Anmeldung stimmst du unseren Nutzungsbedingungen zu.
        </p>
      </div>
    </div>
  );
}
